/**
 * RealAdapter HTTP helpers
 *
 * Shared HTTP functions used by every RealAdapter method.
 * Centralizes: authenticated requests, query string building, and
 * error normalization into AdapterError shape.
 *
 * All real-mode data flows through authenticatedApiRequest() which
 * handles token injection, X-Request-Id, and response parsing.
 *
 * Response cache (R4): realGet memoizes responses keyed by endpoint+params
 * for a short TTL so tab-switch patterns (Companies → People → Companies)
 * don't re-pay the backend cost on every nav. Concurrent callers for the
 * same key share one in-flight request. Any mutation (realPost / realPatch)
 * clears the cache so the next read is fresh — coarse invalidation, but
 * correct for every mutation path in this codebase.
 */

import { authenticatedApiRequest, isApiError } from "../apiClient"
import type { ApiError } from "../apiClient"
import type { AdapterError } from "./IDataAdapter"

// ---------------------------------------------------------------------------
// Response cache (module-scope). Coarse — cleared on any mutation.
// TTL is intentionally short: stale tab-switches are cheap to tolerate;
// stale mutations-in-another-tab would not be. Import-driven flows that
// need fresh data post-mutation already call load() after the mutation
// completes; those loads now hit the backend since the mutation cleared
// the cache.
// ---------------------------------------------------------------------------

const CACHE_TTL_MS = 30_000

type CacheEntry = { data: unknown; expiry: number }

const responseCache = new Map<string, CacheEntry>()
const inFlightGets = new Map<string, Promise<unknown>>()

function cacheKey(endpoint: string, params?: Record<string, unknown>): string {
  // Params are already stable-ordered in buildQuery via URLSearchParams; for
  // the cache key we use JSON of the filtered params to get deterministic
  // keys regardless of property insertion order.
  if (!params) return endpoint
  const filtered: Record<string, unknown> = {}
  for (const k of Object.keys(params).sort()) {
    const v = params[k]
    if (v !== undefined && v !== null) filtered[k] = v
  }
  return `${endpoint}|${JSON.stringify(filtered)}`
}

/** Drop every cached GET response. Called automatically after any mutation. */
export function invalidateResponseCache(): void {
  responseCache.clear()
  inFlightGets.clear()
}

/**
 * Build a query string from params object.
 * Omits undefined/null values. Returns empty string if no params.
 */
export function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return ""
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null
  )
  if (entries.length === 0) return ""
  const qs = new URLSearchParams()
  for (const [key, value] of entries) {
    qs.append(key, String(value))
  }
  return `?${qs.toString()}`
}

/**
 * Convert an ApiError (from apiClient) to an AdapterError (adapter contract).
 */
function toAdapterError(err: ApiError): AdapterError {
  return {
    status: err.status,
    code: err.code ?? `HTTP_${err.status}`,
    message: err.message,
    details: err.details,
  }
}

/**
 * Authenticated GET request.
 * Returns parsed JSON body typed as T.
 * Throws AdapterError on failure.
 *
 * Cached for CACHE_TTL_MS; concurrent requests for the same key share one
 * network call. See module-level notes.
 */
export async function realGet<T, P extends object = Record<string, unknown>>(
  endpoint: string,
  params?: P
): Promise<T> {
  const paramObj = params as Record<string, unknown> | undefined
  const key = cacheKey(endpoint, paramObj)
  const now = Date.now()

  const cached = responseCache.get(key)
  if (cached && now < cached.expiry) {
    return cached.data as T
  }

  const existing = inFlightGets.get(key)
  if (existing) {
    return existing as Promise<T>
  }

  const url = `${endpoint}${buildQuery(paramObj)}`
  const promise = (async () => {
    try {
      const data = await authenticatedApiRequest<T>(url, { method: "GET" })
      responseCache.set(key, { data, expiry: Date.now() + CACHE_TTL_MS })
      return data
    } catch (err) {
      if (isApiError(err)) throw toAdapterError(err)
      throw err
    } finally {
      inFlightGets.delete(key)
    }
  })()

  inFlightGets.set(key, promise)
  return promise
}

/**
 * Authenticated POST request with JSON body.
 * Returns parsed JSON body typed as T.
 * Throws AdapterError on failure.
 */
export async function realPost<T>(
  endpoint: string,
  body?: unknown
): Promise<T> {
  try {
    const data = await authenticatedApiRequest<T>(endpoint, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    invalidateResponseCache()
    return data
  } catch (err) {
    if (isApiError(err)) throw toAdapterError(err)
    throw err
  }
}

/**
 * Authenticated PATCH request with JSON body.
 * Returns parsed JSON body typed as T.
 * Throws AdapterError on failure.
 */
export async function realPatch<T>(
  endpoint: string,
  body?: unknown
): Promise<T> {
  try {
    const data = await authenticatedApiRequest<T>(endpoint, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    invalidateResponseCache()
    return data
  } catch (err) {
    if (isApiError(err)) throw toAdapterError(err)
    throw err
  }
}

/**
 * DELETE wrapper — same auth + error handling as the other verbs.
 * Returns parsed JSON when the server responds with a body, undefined
 * for 204 No Content.
 */
export async function realDelete<T = void>(endpoint: string): Promise<T> {
  try {
    const data = await authenticatedApiRequest<T>(endpoint, {
      method: "DELETE",
    })
    invalidateResponseCache()
    return data
  } catch (err) {
    if (isApiError(err)) throw toAdapterError(err)
    throw err
  }
}
